import { memo, useMemo, useRef, useState } from 'react'

import { DatePicker } from 'core-components/library/datepicker'
import { Dropdown } from 'core-components/library/dropdown'
import { Input } from 'core-components/library/inputs'
import { MultiSelect } from 'core-components/library/multiSelect'
import { BaseCrud, Static } from 'core-components/types'

import { getMultiSelectFilterValue, isEmptyFilterValue } from '../../lib/filterValue'
import { DateRangeValue, FilterConfig, FilterValue, RangeValue } from '../../types'

import styles from './FilterRow.module.scss'

interface FilterRowProps {
  config: FilterConfig
  value: FilterValue
  onChange: (value: FilterValue) => void
  onClear: () => void
  defaultExpanded?: boolean
}

export const FilterRow = memo<FilterRowProps>(
  ({ config, value, onChange, onClear, defaultExpanded = false }) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded)
    const hasValue = !isEmptyFilterValue(value)
    const rootRef = useRef<HTMLDivElement>(null)

    const toggleExpanded = () => {
      setIsExpanded((prev) => {
        const newExpanded = !prev
        // Scroll into view after expanding (wait for CSS transition to complete)
        if (newExpanded) {
          setTimeout(() => {
            rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }, 120)
        }
        return newExpanded
      })
    }

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation()
      onClear()
    }

    const dropdownSource = useMemo(() => {
      if ((config.type === 'select' || config.type === 'multiSelect') && config.options) {
        const source = new BaseCrud({
          fetcher: new Static(
            config.options.map((opt) => ({
              value: opt.value,
              text: opt.label,
            }))
          ),
          keyField: 'value',
        })

        source.setPaging({ page: 0, pageSize: 10000 })

        return source
      }
      return null
    }, [config.type, config.options])

    const renderEditor = () => {
      switch (config.type) {
        case 'text':
          return (
            <Input
              value={(value as string) || ''}
              onChange={(val) => onChange(val || null)}
              placeholder={config.placeholder || 'Search'}
              startContent={<i className="kbase kbase-search" />}
            />
          )

        case 'select':
          if (!dropdownSource) return null
          return (
            <Dropdown
              source={dropdownSource}
              value={(value as string | number) || ''}
              onChange={(val) => onChange(val || null)}
              caption="text"
              placeholder={config.placeholder || `Select ${config.label.toLowerCase()}...`}
            />
          )

        case 'multiSelect':
          if (!dropdownSource) return null
          return (
            <MultiSelect
              caption="text"
              closeOnSelect={false}
              noFilterOnSelect
              onChange={(nextValue) => onChange(nextValue.length ? nextValue : null)}
              placeholder={config.placeholder || `Select ${config.label.toLowerCase()}...`}
              source={dropdownSource}
              value={getMultiSelectFilterValue(value)}
            />
          )

        case 'date':
          return (
            <DatePicker
              mode="single"
              value={value instanceof Date ? value : undefined}
              onChange={(date) => onChange(date)}
              label={config.placeholder}
            />
          )

        case 'dateRange': {
          const dateRangeVal = (value as DateRangeValue) || { from: null, to: null }
          return (
            <div className={styles.rangeInputs}>
              <div className={styles.rangeField}>
                <span className={styles.rangeLabel}>From:</span>
                <DatePicker
                  mode="single"
                  value={dateRangeVal.from || undefined}
                  onChange={(date) => onChange({ ...dateRangeVal, from: date || null })}
                />
              </div>
              <div className={styles.rangeField}>
                <span className={styles.rangeLabel}>To:</span>
                <DatePicker
                  mode="single"
                  value={dateRangeVal.to || undefined}
                  onChange={(date) => onChange({ ...dateRangeVal, to: date || null })}
                />
              </div>
            </div>
          )
        }

        case 'range': {
          const rangeVal = (value as RangeValue) || { from: null, to: null }
          return (
            <div className={styles.rangeInputs}>
              <div className={styles.rangeField}>
                <span className={styles.rangeLabel}>From:</span>
                <Input
                  value={rangeVal.from !== null ? String(rangeVal.from) : ''}
                  onChange={(val) => onChange({ ...rangeVal, from: val || null })}
                  placeholder={config.placeholder || 'Min'}
                />
              </div>
              <div className={styles.rangeField}>
                <span className={styles.rangeLabel}>To:</span>
                <Input
                  value={rangeVal.to !== null ? String(rangeVal.to) : ''}
                  onChange={(val) => onChange({ ...rangeVal, to: val || null })}
                  placeholder="Max"
                />
              </div>
            </div>
          )
        }

        default:
          return null
      }
    }

    return (
      <div className={styles.root} data-expanded={isExpanded} ref={rootRef}>
        <div className={styles.header} onClick={toggleExpanded}>
          <div className={styles.headerLeft}>
            <i className={`kbase kbase-chevron-right ${styles.chevron}`} />
            <span className={styles.label}>{config.label}</span>
          </div>
          {hasValue && (
            <button className={styles.clearButton} onClick={handleClear} type="button">
              Clear
            </button>
          )}
        </div>
        <div className={styles.editor}>
          <div className={styles.editorContent}>{renderEditor()}</div>
        </div>
      </div>
    )
  }
)

FilterRow.displayName = 'FilterRow'
